import { useState } from 'react';
import { phoneField } from '../phone';
import { TeknikaMark } from '../components/Brand';
import { put } from '../api';
import { describe } from '../errors';
import { usePortalLoad } from './offline';

const KEY = (id: number) => `teknika_portal_welcomed_${id}`;

/** Whether this person has been through the first-run screens on this phone. */
export function onboarded(userId: number): boolean {
  try {
    return localStorage.getItem(KEY(userId)) === '1';
  } catch {
    return true; // Storage blocked: never trap someone behind a welcome they cannot dismiss.
  }
}

interface Me {
  name: string;
  mobile: string;
  office_phone: string;
}

const STEPS = [
  {
    title: (name: string) => `Welcome${name ? `, ${name.split(' ')[0]}` : ''}`,
    body: 'This is where you see your visits, tick off tasks and pick up extra work. It works on your phone, even with a poor signal.',
    cta: 'Next',
  },
  {
    title: () => 'How it works',
    body: 'Confirm each visit so the office knows you have seen it. Tick the tasks as you go. If you lead a visit, mark it complete when you are done. Anything you do without a signal is saved and sent when you are back online.',
    cta: 'Next',
  },
  {
    title: () => 'Check your details',
    // Replaced below when there is no number yet: asking to confirm an empty field reads oddly.
    body: 'Is this the best number to reach you on?',
    cta: 'Get started',
  },
];

/** Three short screens shown once: welcome, how it works, and a check of the contact number. */
export function Onboarding(props: { name: string; userId: number; onDone(): void }) {
  const [step, setStep] = useState(0);
  const me = usePortalLoad<Me>('/api/portal/me');
  const [mobile, setMobile] = useState<string | null>(null);
  const [error, setError] = useState('');
  const s = STEPS[step]!;
  const last = step === STEPS.length - 1;
  const known = (me.data?.mobile ?? '').trim() !== '';
  const body =
    last && me.data && !known
      ? 'Add a mobile number, so the office can reach you on the day of a visit.'
      : s.body;

  async function next() {
    if (!last) {
      setStep(step + 1);
      return;
    }
    setError('');
    const value = (mobile ?? me.data?.mobile ?? '').trim();
    try {
      if (value !== (me.data?.mobile ?? '')) await put('/api/portal/profile', { mobile: value });
    } catch (e) {
      setError(describe(e).problems[0] ?? 'Could not save your number.');
      return;
    }
    try {
      localStorage.setItem(KEY(props.userId), '1');
    } catch {
      // The screens may show again next time; that is all.
    }
    props.onDone();
  }

  return (
    <main className="p-onboard">
      <TeknikaMark size="2.9rem" />
      <h1>{s.title(props.name)}</h1>
      <p key={step}>{body}</p>
      {last && (
        <>
          <label>
            Mobile number
            <input
              inputMode="tel"
              autoComplete="tel"
              value={mobile ?? me.data?.mobile ?? ''}
              onChange={(e) => setMobile(phoneField(e.target.value))}
            />
          </label>
          <p className="muted">The office is the only one who sees this.</p>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
        </>
      )}
      <p className="sr-only" aria-live="polite">
        Step {step + 1} of {STEPS.length}
      </p>
      <div className="p-dots" aria-hidden="true">
        {STEPS.map((_, i) => (
          <span key={i} className={i === step ? 'on' : ''} />
        ))}
      </div>
      <button type="button" className="p-big" onClick={() => void next()}>
        {s.cta}
      </button>
    </main>
  );
}
